/**
 * SriLankaHero - Sri Lanka-only redesigned homepage hero.
 * Renders a premium cityscape hero with trust badges, category pill navigation
 * and a stats bar. Content is admin-editable via the localization API.
 * See: GET /api/admin/localization/public/content/SL
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Zap,
  MapPin,
  Star,
  Users,
  Package,
  Building2,
  Home as HomeIcon,
  Car,
  ShoppingBag,
  Armchair,
  Briefcase,
  Wrench,
  Shirt,
  Dumbbell,
  MoreHorizontal,
} from 'lucide-react';

// Category pills - icons, colors and fills match the Sri Lanka mockup.
const DEFAULT_PILLS = [
  { name: 'Property', slug: 'property', icon: HomeIcon, color: 'text-blue-500', fill: true },
  { name: 'Vehicles', slug: 'vehicles', icon: Car, color: 'text-purple-500', fill: true },
  { name: 'Electronics', slug: 'electronics-computers', icon: ShoppingBag, color: 'text-emerald-500', fill: true },
  { name: 'Home & Garden', slug: 'home-garden', icon: Armchair, color: 'text-pink-500', fill: true },
  { name: 'Jobs', slug: 'jobs', icon: Briefcase, color: 'text-blue-500', fill: true },
  { name: 'Services', slug: 'services-trades', icon: Wrench, color: 'text-blue-500', fill: true },
  { name: 'Fashion', slug: 'fashion-beauty', icon: Shirt, color: 'text-purple-500', fill: true },
  { name: 'Business & Industrial', slug: 'business-industry', icon: Building2, color: 'text-blue-500', fill: true },
  { name: 'Sports & Hobbies', slug: 'sports-fitness-hobbies', icon: Dumbbell, color: 'text-pink-500', fill: false },
  { name: 'More', slug: 'all', icon: MoreHorizontal, color: 'text-slate-500', fill: false },
];

const DEFAULT_STATS = [
  { label: 'Active Users', value: '500K+', icon: Users, iconBg: 'bg-purple-100', iconColor: 'text-purple-600', fill: true },
  { label: 'Listings', value: '100K+', icon: Package, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', fill: false },
  { label: 'Nationwide Coverage', value: 'All 25 Districts', icon: MapPin, iconBg: 'bg-rose-100', iconColor: 'text-rose-500', fill: true },
  { label: 'User Satisfaction', value: '4.8/5', icon: Star, iconBg: 'bg-amber-100', iconColor: 'text-amber-500', fill: true },
];

const TRUST_MARKERS = [
  { label: 'Verified Sellers', icon: ShieldCheck, iconColor: 'text-white', iconBg: 'bg-blue-500', fill: false, strokeWidth: 3 },
  { label: 'Safe & Secure', icon: Zap, iconColor: 'text-amber-500', iconBg: 'bg-transparent', fill: true },
  { label: 'Nationwide Search', icon: MapPin, iconColor: 'text-rose-500', iconBg: 'bg-transparent', fill: true },
  { label: 'Trusted by Sri Lankans', icon: Star, iconColor: 'text-yellow-500', iconBg: 'bg-transparent', fill: true },
];

// Sri Lanka hero backdrop - user-supplied clean marketplace image
// (Colombo sunset skyline + Beira Lake + palm trees + Lotus Tower + mascot with floating listing cards).
const HERO_BG =
  'https://customer-assets-4nw71qhi.emergentagent.net/job_0c7a69bb-ed41-420f-925e-54e2fc86c5bf/artifacts/ps0eeb65_zeilian-hero-background-clean.png';

const SriLankaHero = ({ content = {}, stats: statsOverride, pills: pillsOverride }) => {
  const navigate = useNavigate();
  const pills = pillsOverride && pillsOverride.length ? pillsOverride : DEFAULT_PILLS;
  const stats = statsOverride && statsOverride.length ? statsOverride : DEFAULT_STATS;

  const badge = content.trust_badge || "SRI LANKA'S TRUSTED MARKETPLACE";
  const title = content.sl_hero_title || 'Everything You Need';
  const titleAccent = content.sl_hero_title_accent || 'in Sri Lanka';
  const titleTail = content.sl_hero_title_tail || ', In One Place.';
  const subtitle =
    content.sl_hero_subtitle ||
    'Buy, sell, rent, find services and discover opportunities - all across Sri Lanka. Fast. Trusted. Local.';

  return (
    <section
      className="relative overflow-hidden"
      data-testid="sl-hero"
      aria-label="Sri Lanka homepage hero"
    >
      {/* Full-bleed clean backdrop image (Colombo sunset + girl + floating listing cards) */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center center' }}
          loading="eager"
        />
        {/* Subtle bottom vignette only - keep the vibrant sunset scene edge to edge */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-10 pb-8 md:pt-14 md:pb-10">
        {/* Trust badge pill */}
        <div className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-4 py-1.5 shadow-sm ring-1 ring-slate-200">
          <span className="text-lg leading-none notranslate" translate="no">🇱🇰</span>
          <span
            className="text-[11px] md:text-xs font-semibold tracking-wide text-slate-800"
            data-testid="sl-hero-trust-badge"
          >
            {badge}
          </span>
          <span className="text-yellow-500 notranslate" translate="no">👑</span>
        </div>

        {/* Big headline */}
        <h1
          className="mt-4 font-extrabold text-slate-900 tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[3.5rem] max-w-[42rem]"
          data-testid="sl-hero-title"
        >
          {title}
          <br />
          <span
            className="bg-gradient-to-r from-fuchsia-600 via-purple-600 to-purple-700 bg-clip-text text-transparent"
            style={{ WebkitTextStroke: '0px' }}
          >
            {titleAccent}
          </span>
          {titleTail}
        </h1>

        {/* Subtitle */}
        <p
          className="mt-4 text-slate-800 text-base md:text-lg max-w-2xl leading-relaxed font-medium"
          data-testid="sl-hero-subtitle"
        >
          {subtitle}
        </p>

        {/* Trust markers row - filled icons, single row on desktop */}
        <div
          className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2"
          data-testid="sl-hero-trust-markers"
        >
          {TRUST_MARKERS.map((marker) => {
            const Icon = marker.icon;
            return (
              <div key={marker.label} className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${marker.iconBg}`}
                >
                  <Icon
                    className={`h-4 w-4 ${marker.iconColor}`}
                    strokeWidth={marker.strokeWidth || 2.5}
                    fill={marker.fill ? 'currentColor' : 'none'}
                  />
                </span>
                <span className="text-sm font-semibold text-slate-800">
                  {marker.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Category pill row - larger tiles matching the mockup */}
        <div
          className="mt-8 grid grid-cols-4 sm:grid-cols-5 md:grid-cols-10 gap-3"
          data-testid="sl-hero-category-pills"
        >
          {pills.map((pill) => {
            const Icon = pill.icon || Building2;
            return (
              <button
                key={pill.slug}
                onClick={() => navigate(`/category/${pill.slug}`)}
                className="group flex flex-col items-center justify-center gap-2 rounded-2xl bg-white px-2 py-4 shadow-md ring-1 ring-slate-100 hover:shadow-lg hover:-translate-y-0.5 hover:ring-slate-200 transition-all"
                data-testid={`sl-hero-pill-${pill.slug}`}
                aria-label={`Browse ${pill.name}`}
              >
                <Icon
                  className={`h-7 w-7 ${pill.color}`}
                  strokeWidth={2.25}
                  fill={pill.fill ? 'currentColor' : 'none'}
                />
                <span className="text-[11px] md:text-[13px] font-semibold text-slate-800 text-center leading-tight line-clamp-2">
                  {pill.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* Stats strip - pill card matching the mockup bottom bar */}
        <div
          className="mt-6 rounded-full bg-white/95 backdrop-blur-sm shadow-md ring-1 ring-slate-100 px-6 py-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          data-testid="sl-hero-stats"
        >
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="flex items-center gap-3">
                <span className={`inline-flex items-center justify-center h-11 w-11 rounded-2xl ${stat.iconBg}`}>
                  <Icon className={`h-5 w-5 ${stat.iconColor}`} strokeWidth={2.25} fill={stat.fill ? 'currentColor' : 'none'} />
                </span>
                <div className="flex flex-col leading-tight">
                  <span className="text-lg md:text-xl font-extrabold text-slate-900 notranslate" translate="no">
                    {stat.value}
                  </span>
                  <span className="text-xs md:text-sm text-slate-500 font-medium">{stat.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SriLankaHero;
